/**
Project: Voice Panel (c)
Title: Routing Policy List 
Description: Component to handle the map based on the selected regions.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { memo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import MapData from "../settings/map-data.json";
import { VoiceMapSettings } from "../settings/map-settings";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";

const VoiceRouteMap = ({ voiceRoutes }) => {
  let MapSettings = VoiceMapSettings;
  /*
  if (window && window.simplemaps_worldmap_mapdata) {
    MapSettings = window.simplemaps_worldmap_mapdata;
  }
  */
  const getcoveredAreas = (geographies) => {
    let coveredCountryList = [];
    const countryKeys = Object.keys(MapSettings.state_specific);
    const allCountries = MapSettings.state_specific;
    countryKeys.forEach((country) => {
      allCountries[country].ISO_A2 = country;
    });
    const countryValues = Object.values(allCountries);
    const coveredCountries = countryValues.filter(
      (item) =>
        item.description === "Full PSTN Replacement" ||
        item.description === "Limited Coverage"
    );

    geographies.forEach((element) => {
      coveredCountries.forEach((country) => {
        if (element.properties.ISO_A2 === country.ISO_A2) {
          element.properties["Color"] = country.color;
          // element.properties["HoverColor"] = country.hover_color
          element.properties["description"] = country.description;
          coveredCountryList.push(element);
        }
      });
    });
    return coveredCountryList;
  };

  const getUncoveredAreas = (geographies) => {
    let uncoveredCountryList = [];
    const countryKeys = Object.keys(MapSettings.state_specific);
    const allCountries = MapSettings.state_specific;
    countryKeys.forEach((country) => {
      allCountries[country].ISO_A2 = country;
    });
    const countryValues = Object.values(allCountries);
    const notCoveredCountries = countryValues.filter(
      (item) => item.description === "No Coverage"
    );

    geographies.forEach((element) => {
      notCoveredCountries.forEach((country) => {
        if (element.properties.ISO_A2 === country.ISO_A2) {
          element.properties["description"] = country.description;
          uncoveredCountryList.push(element);
        }
      });
    });
    return uncoveredCountryList;
  };

  const mapstatus = (data) => {
    if (data.routeStatus === 0) {
      return VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.UNASSIGNED;
    }
    if (data.routePriority === 1) {
      return VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.PRIMARY;
    }
    return VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.FAILOVER;
  };

  const setMarkerColor = (status) => {
    const routingStatus = {
      [VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.PRIMARY]:
        VoiceAdminConstants.VOICE_ROUTES_MAP.COLOR.MARKER_PRIMARY,
      [VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.FAILOVER]:
        VoiceAdminConstants.VOICE_ROUTES_MAP.COLOR.MARKER_FAILOVER,
      [VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.UNASSIGNED]:
        VoiceAdminConstants.VOICE_ROUTES_MAP.COLOR.MARKER_UNASSIGNED,
    };
    return (
      routingStatus[status] ||
      routingStatus[VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.UNASSIGNED]
    );
  };

  return (
    <div className="vpa-routing-map-wrapper">
      <ComposableMap
        data-tip=""
        data-text-color="black"
        data-background-color={MapSettings.main_settings.popup_color}
        projection="geoNaturalEarth1"
        width={900}
        height={600}
      >
        <Geographies geography={MapData}>
          {({ geographies }) => {
            const list = getcoveredAreas(geographies);
            return list.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                style={{
                  default: {
                    fill: geo.properties.Color,
                    outline: "none",
                  },
                  hover: {
                    fill: geo.properties.Color,
                    outline: "none",
                  },
                  pressed: {
                    fill: geo.properties.Color,
                    outline: "none",
                  },
                }}
              />
            ));
          }}
        </Geographies>
        <Geographies geography={MapData}>
          {({ geographies }) => {
            const list = getUncoveredAreas(geographies);
            return list.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                style={{
                  default: {
                    fill: MapSettings.main_settings.state_color,
                    outline: "none",
                  },
                  hover: {
                    fill: MapSettings.main_settings.state_color,
                    outline: "none",
                  },
                  pressed: {
                    fill: MapSettings.main_settings.state_color,
                    outline: "none",
                  },
                }}
              />
            ));
          }}
        </Geographies>
        {voiceRoutes.map((data, i) => {
          return (
            <Marker key={i} coordinates={[data.longitude, data.latitude]}>
              {mapstatus(data) ===
              VoiceAdminConstants.VOICE_ROUTES_MAP.STATUS.PRIMARY ? (
                <rect
                  x={-9}
                  y={-9}
                  transform="rotate(45)"
                  width={18}
                  height={18}
                  fill={setMarkerColor(mapstatus(data))}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ) : (
                <circle
                  r={9}
                  fill={setMarkerColor(mapstatus(data))}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}
            </Marker>
          );
        })}
      </ComposableMap>
    </div>
  );
};

export default memo(VoiceRouteMap);
